/**
 * Утилиты для обработки вставки данных из Excel в табличные формы
 */

export interface PasteData {
  rows: string[][];
  startRow: number;
  startCol: number;
}

export interface EditableCell {
  rowIndex: number;
  colIndex: number;
  dataField: string;
  isEditable: boolean;
  currentValue: any;
}

export type EditableCellMatrix = EditableCell[][];

export interface PasteResult {
  success: boolean;
  message?: string;
  updatedCells: EditableCell[];
}

/**
 * Парсит данные из буфера обмена Excel
 */
export function parseExcelPasteData(clipboardData: string): PasteData | null {
  try {
    // Разбиваем данные по строкам
    const rows = clipboardData
      .split(/\r?\n/)
      .filter(row => row.trim() !== '')
      .map(row => 
        row.split('\t').map(cell => cell.trim())
      );

    if (rows.length === 0) {
      return null;
    }

    return {
      rows,
      startRow: 0,
      startCol: 0
    };
  } catch (error) {
    console.error('Ошибка парсинга данных из Excel:', error);
    return null;
  }
}

/**
 * Проверяет, является ли ячейка редактируемой
 */
export function isCellEditable(
  cell: any, 
  editingPermissions: any, 
  acceptPeriods: any, 
  periodName: string,
  blockId?: string
): boolean {
  // Проверяем права на редактирование
  if (!editingPermissions) {
    return false;
  }

  // Проверяем акцепт для периода
  if (acceptPeriods && acceptPeriods[periodName]) {
    return false;
  }

  // Проверяем блокировку для конкретного блока
  if (blockId && editingPermissions[`editing__ok_hn__${blockId}`] === false) {
    return false;
  }

  return true;
}

/**
 * Применяет данные из Excel к таблице (2D версия)
 */
export function applyExcelData2D(
  pasteData: PasteData,
  tableData: any,
  editableCellsMatrix: EditableCellMatrix,
  startCell: EditableCell | null
): PasteResult {
  const updatedCells: EditableCell[] = [];
  let successCount = 0;
  let skipCount = 0;

  console.log('🚀 applyExcelData2D called');
  console.log('🚀 startCell:', startCell);
  console.log('🚀 editableCellsMatrix dimensions:', editableCellsMatrix.length, 'x', editableCellsMatrix[0]?.length);

  try {
    // Проверяем, что стартовая ячейка задана
    if (!startCell) {
      console.log('❌ startCell is null');
      return {
        success: false,
        message: 'Не выбрана стартовая ячейка для вставки. Кликните на ячейку перед вставкой.',
        updatedCells: []
      };
    }

    // Проверяем, что матрица редактируемых ячеек не пустая
    if (!editableCellsMatrix || editableCellsMatrix.length === 0) {
      return {
        success: false,
        message: 'Нет доступных ячеек для редактирования',
        updatedCells: []
      };
    }

    // Находим координаты стартовой ячейки в матрице
    let startRow = -1;
    let startCol = -1;
    
    for (let rowIndex = 0; rowIndex < editableCellsMatrix.length; rowIndex++) {
      const row = editableCellsMatrix[rowIndex];
      for (let colIndex = 0; colIndex < row.length; colIndex++) {
        const cell = row[colIndex];
        if (cell.dataField === startCell.dataField) {
          startRow = rowIndex;
          startCol = colIndex;
          break;
        }
      }
      if (startRow !== -1) break;
    }

    if (startRow === -1 || startCol === -1) {
      return {
        success: false,
        message: 'Стартовая ячейка не найдена в редактируемых ячейках',
        updatedCells: []
      };
    }

    console.log(`🚀 Start position: row ${startRow}, col ${startCol}`);

    // Проходим по данным из Excel
    for (let rowIndex = 0; rowIndex < pasteData.rows.length; rowIndex++) {
      const excelRow = pasteData.rows[rowIndex];
      const targetRow = startRow + rowIndex;
      
      console.log(`📋 Processing Excel row ${rowIndex}:`, excelRow);
      
      for (let colIndex = 0; colIndex < excelRow.length; colIndex++) {
        const excelValue = excelRow[colIndex];
        const targetCol = startCol + colIndex;
        
        console.log(`📋 Processing value "${excelValue}" at matrix position (${targetRow}, ${targetCol})`);
        
        // Проверяем, что целевая позиция находится в пределах матрицы
        if (targetRow < editableCellsMatrix.length && 
            targetCol < editableCellsMatrix[targetRow].length) {
          
          const targetCell = editableCellsMatrix[targetRow][targetCol];
          
          if (targetCell && targetCell.isEditable) {
            // Преобразуем значение в число
            const numericValue = parseFloat(excelValue);
            
            if (!isNaN(numericValue)) {
              // Обновляем значение в данных
              updateCellValue(tableData, targetCell, numericValue);
              
              updatedCells.push({
                ...targetCell,
                currentValue: numericValue
              });
              
              successCount++;
              console.log(`✅ Updated ${targetCell.dataField} to ${numericValue}`);
            } else {
              skipCount++;
              console.log(`⏭️ Skipped non-numeric value: "${excelValue}"`);
            }
          } else {
            skipCount++;
            console.log(`⏭️ Skipped non-editable cell: ${targetCell?.dataField || 'unknown'}`);
          }
        } else {
          console.log(`⏭️ Target position (${targetRow}, ${targetCol}) out of matrix bounds`);
        }
      }
    }

    return {
      success: true,
      message: `Успешно обновлено ${successCount} ячеек, пропущено ${skipCount} ячеек`,
      updatedCells
    };

  } catch (error) {
    console.error('Ошибка применения данных из Excel:', error);
    return {
      success: false,
      message: 'Ошибка при применении данных из Excel',
      updatedCells: []
    };
  }
}

/**
 * Применяет данные из Excel к таблице (1D версия для обратной совместимости)
 */
export function applyExcelData(
  pasteData: PasteData,
  tableData: any,
  editableCells: EditableCell[],
  startCell: EditableCell | null
): PasteResult {
  const updatedCells: EditableCell[] = [];
  let successCount = 0;
  let skipCount = 0;

  console.log('🚀 applyExcelData called');
  console.log('🚀 startCell:', startCell);
  console.log('🚀 editableCells length:', editableCells?.length);

  try {
    // Проверяем, что стартовая ячейка задана
    if (!startCell) {
      console.log('❌ startCell is null');
      return {
        success: false,
        message: 'Не выбрана стартовая ячейка для вставки. Кликните на ячейку перед вставкой.',
        updatedCells: []
      };
    }

    // Проверяем, что массив редактируемых ячеек не пустой
    if (!editableCells || editableCells.length === 0) {
      return {
        success: false,
        message: 'Нет доступных ячеек для редактирования',
        updatedCells: []
      };
    }

    // Находим индекс стартовой ячейки в массиве редактируемых ячеек
    const startIndex = editableCells.findIndex(cell => 
      cell && cell.rowIndex === startCell.rowIndex && cell.colIndex === startCell.colIndex
    );

    if (startIndex === -1) {
      return {
        success: false,
        message: 'Стартовая ячейка не найдена в редактируемых ячейках',
        updatedCells: []
      };
    }

    // Проходим по данным из Excel
    for (let rowIndex = 0; rowIndex < pasteData.rows.length; rowIndex++) {
      const excelRow = pasteData.rows[rowIndex];
      
      for (let colIndex = 0; colIndex < excelRow.length; colIndex++) {
        const excelValue = excelRow[colIndex];
        
        // Вычисляем индекс целевой ячейки: начинаем с startIndex и идем по порядку
        const targetIndex = startIndex + rowIndex * excelRow.length + colIndex;
        
        console.log(`📋 Processing value "${excelValue}" at targetIndex ${targetIndex} (row:${rowIndex}, col:${colIndex})`);
        
        if (targetIndex < editableCells.length) {
          const targetCell = editableCells[targetIndex];
          
          if (targetCell && targetCell.isEditable) {
            // Преобразуем значение в число
            const numericValue = parseFloat(excelValue);
            
            if (!isNaN(numericValue)) {
              // Обновляем значение в данных
              updateCellValue(tableData, targetCell, numericValue);
              
              updatedCells.push({
                ...targetCell,
                currentValue: numericValue
              });
              
              successCount++;
              console.log(`✅ Updated ${targetCell.dataField} to ${numericValue}`);
            } else {
              skipCount++;
              console.log(`⏭️ Skipped non-numeric value: "${excelValue}"`);
            }
          } else {
            skipCount++;
            console.log(`⏭️ Skipped non-editable cell: ${targetCell?.dataField || 'unknown'}`);
          }
        } else {
          console.log(`⏭️ Target index ${targetIndex} out of range (max: ${editableCells.length - 1})`);
        }
      }
    }

    return {
      success: true,
      message: `Успешно обновлено ${successCount} ячеек, пропущено ${skipCount} ячеек`,
      updatedCells
    };

  } catch (error) {
    console.error('Ошибка применения данных из Excel:', error);
    return {
      success: false,
      message: 'Ошибка при применении данных из Excel',
      updatedCells: []
    };
  }
}

/**
 * Обновляет значение ячейки в данных таблицы
 */
function updateCellValue(tableData: any, cell: EditableCell, value: number): void {
  // Разбираем путь к данным по dataField
  const path = cell.dataField.split('.');
  let current = tableData;
  
  // Проходим по пути до предпоследнего элемента
  for (let i = 0; i < path.length - 1; i++) {
    if (current[path[i]] === undefined) {
      current[path[i]] = {};
    }
    current = current[path[i]];
  }
  
  // Устанавливаем значение
  current[path[path.length - 1]] = value;
}

/**
 * Создает массив редактируемых ячеек для таблицы
 */
export function createEditableCellsMap(
  tableData: any,
  editingPermissions: any,
  acceptPeriods: any,
  periodName: string,
  blockId?: string
): EditableCell[] {
  const editableCells: EditableCell[] = [];
  
  // Рекурсивно обходим структуру данных
  function traverseData(data: any, path: string = '', rowIndex: number = 0, colIndex: number = 0) {
    if (typeof data === 'object' && data !== null) {
      for (const key in data) {
        const currentPath = path ? `${path}.${key}` : key;
        
        // Проверяем, является ли это числовым значением
        if (typeof data[key] === 'number') {
          const isEditable = isCellEditable(data, editingPermissions, acceptPeriods, periodName, blockId);
          
          editableCells.push({
            rowIndex,
            colIndex,
            dataField: currentPath,
            isEditable,
            currentValue: data[key]
          });
          
          colIndex++;
        } else if (typeof data[key] === 'object') {
          // Рекурсивно обрабатываем вложенные объекты
          traverseData(data[key], currentPath, rowIndex, colIndex);
        }
      }
    }
  }
  
  traverseData(tableData);
  return editableCells;
}

/**
 * Обрабатывает событие вставки из буфера обмена (2D версия)
 */
export function handlePasteEvent2D(
  event: ClipboardEvent,
  tableData: any,
  editableCellsMatrix: EditableCellMatrix,
  startCell: EditableCell | null
): PasteResult | null {
  const clipboardData = event.clipboardData?.getData('text');
  
  if (!clipboardData) {
    return {
      success: false,
      message: 'Нет данных в буфере обмена',
      updatedCells: []
    };
  }

  const pasteData = parseExcelPasteData(clipboardData);
  
  if (!pasteData) {
    return {
      success: false,
      message: 'Не удалось распарсить данные из буфера обмена',
      updatedCells: []
    };
  }

  return applyExcelData2D(pasteData, tableData, editableCellsMatrix, startCell);
}

/**
 * Обрабатывает событие вставки из буфера обмена (1D версия для обратной совместимости)
 */
export function handlePasteEvent(
  event: ClipboardEvent,
  tableData: any,
  editableCells: EditableCell[],
  startCell: EditableCell | null
): PasteResult | null {
  const clipboardData = event.clipboardData?.getData('text');
  
  if (!clipboardData) {
    return {
      success: false,
      message: 'Нет данных в буфере обмена',
      updatedCells: []
    };
  }

  const pasteData = parseExcelPasteData(clipboardData);
  
  if (!pasteData) {
    return {
      success: false,
      message: 'Не удалось распарсить данные из буфера обмена',
      updatedCells: []
    };
  }

  return applyExcelData(pasteData, tableData, editableCells, startCell);
}